import * as React from 'react';
import Icon from '../Icon';
import S from './TextButton.styled';
import { ILink } from '@/src/types';
import { TextButtonLabel } from '@/styles/typography';

type Props = {
  label?: string;
  link?: ILink;
  internalUrl?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  color?: 'white' | 'black';
  disabled?: boolean;
  size?: string | number;
  sizeMobile?: string | number;
  margin?: string | number;
  arrowLeft?: boolean;
  noArrow?: boolean;
  underline?: boolean;
  inline?: boolean;
};

const TextButton: React.FC<Props> = ({
  label,
  internalUrl,
  link,
  onClick,
  color,
  disabled,
  size,
  sizeMobile,
  margin,
  arrowLeft,
  noArrow,
  underline,
  inline,
}) => {
  const commonProps = {
    $disabled: disabled,
    $color: color,
    $margin: margin,
    $underline: underline,
    $size: size,
    $sizeMobile: sizeMobile,
    $inline: inline,
  };

  const renderContent = (
    <>
      {arrowLeft && (
        <Icon
          src="ArrowLeft"
          width={16}
          color={color || 'black'}
          margin="0 10px 0 0"
        />
      )}
      <TextButtonLabel
        $size={size}
        $sizeMobile={sizeMobile}
        $color={color || 'black'}
        $underline={underline}
      >
        {label}
      </TextButtonLabel>
      {!(arrowLeft || noArrow) && (
        <Icon
          src="ArrowRight"
          width={16}
          color={color || 'black'}
          margin="0 0 0 10px"
        />
      )}
    </>
  );

  if (link && link.url) {
    return (
      <S.ExternalLink
        href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
        target={link.target}
        {...commonProps}
      >
        {renderContent}
      </S.ExternalLink>
    );
  }

  if (link && link.cached_url) {
    return (
      <S.InternalLink
        to={`/${link.cached_url.replace(/^\//, '')}`}
        target={link.target}
        {...commonProps}
      >
        {renderContent}
      </S.InternalLink>
    );
  }

  if (internalUrl)
    return (
      <S.InternalLink to={internalUrl} {...commonProps}>
        {renderContent}
      </S.InternalLink>
    );

  if (onClick)
    return (
      <S.Button onClick={onClick} {...commonProps} tabIndex={0}>
        {renderContent}
      </S.Button>
    );

  return (
    <>
      {label && (
        <S.Button {...commonProps} tabIndex={0}>
          {renderContent}
        </S.Button>
      )}
    </>
  );
};

export default TextButton;
